import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Tooltip,
  Alert,
  Chip,
} from "@mui/material";
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
} from "@mui/icons-material";
import { leaveService } from "../../services/leaveService";
import { LeaveType } from "../../types/leave";

const defaultLeaveType: Partial<LeaveType> = {
  name: "",
  description: "",
};

// Helper function to handle errors consistently
const handleError = (e: unknown) => {
  if (e instanceof Error) return e.message;
  return "An unexpected error occurred";
};

const LeaveTypeManagement: React.FC = () => {
  const [leaveTypes, setLeaveTypes] = useState<LeaveType[]>([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingType, setEditingType] =
    useState<Partial<LeaveType>>(defaultLeaveType);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  useEffect(() => {
    loadLeaveTypes();
  }, []);

  const loadLeaveTypes = async () => {
    try {
      const types = await leaveService.getLeaveTypes();
      setLeaveTypes(types);
    } catch (e) {
      setError(handleError(e));
    }
  };

  const handleOpen = (type?: LeaveType) => {
    setEditingType(type || defaultLeaveType);
    setDialogOpen(true);
  };

  const handleClose = () => {
    setDialogOpen(false);
    setEditingType(defaultLeaveType);
    setError("");
  };

  const handleSave = async () => {
    try {
      if (!editingType.name) {
        setError("Name is required");
        return;
      }

      if (editingType.id) {
        await leaveService.updateLeaveType(editingType.id, editingType);
        setSuccess("Leave type updated successfully");
      } else {
        await leaveService.createLeaveType(editingType);
        setSuccess("Leave type created successfully");
      }

      loadLeaveTypes();
      setDialogOpen(false);
      setEditingType(defaultLeaveType);
    } catch (e) {
      setError(handleError(e));
    }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm("Are you sure you want to delete this leave type?")) {
      return;
    }

    try {
      await leaveService.deleteLeaveType(id);
      setSuccess("Leave type deleted successfully");
      loadLeaveTypes();
    } catch (e) {
      setError(handleError(e));
    }
  };

  return (
    <Box>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess("")}>
          {success}
        </Alert>
      )}

      <Box sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}>
        <Typography variant="h6">Leave Types</Typography>
        <Button
          startIcon={<AddIcon />}
          variant="contained"
          onClick={() => handleOpen()}
        >
          Add Leave Type
        </Button>
      </Box>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Description</TableCell>
              <TableCell align="center">Status</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {leaveTypes.map((type) => (
              <TableRow key={type.id} hover>
                <TableCell>
                  <Typography variant="body1" fontWeight="medium">
                    {type.name}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2" color="textSecondary">
                    {type.description || "No description"}
                  </Typography>
                </TableCell>
                <TableCell align="center">
                  <Chip label="Active" color="success" size="small" />
                </TableCell>
                <TableCell>
                  <Tooltip title="Edit">
                    <IconButton onClick={() => handleOpen(type)} size="small">
                      <EditIcon />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Delete">
                    <IconButton
                      onClick={() => handleDelete(type.id)}
                      size="small"
                      color="error"
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={dialogOpen} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingType.id ? "Edit Leave Type" : "Add Leave Type"}
        </DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mt: 2, mb: 3 }}>
            Leave types are basic templates. Configure entitlements, limits, and
            approval rules in the <strong>Leave Entitlements</strong> section
            for each employee category.
          </Alert>
          <Box sx={{ mt: 2 }}>
            <TextField
              fullWidth
              label="Leave Type Name"
              placeholder="e.g., Casual Leave, Medical Leave"
              value={editingType.name}
              onChange={(e) =>
                setEditingType({ ...editingType, name: e.target.value })
              }
              margin="normal"
              required
              helperText="A unique name for this leave type"
            />
            <TextField
              fullWidth
              label="Description"
              placeholder="Brief description of when this leave type is used"
              value={editingType.description}
              onChange={(e) =>
                setEditingType({ ...editingType, description: e.target.value })
              }
              margin="normal"
              multiline
              rows={3}
              helperText="Optional: Describe when employees should use this leave type"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Cancel</Button>
          <Button onClick={handleSave} variant="contained">
            {editingType.id ? "Update" : "Create"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default LeaveTypeManagement;
